import React, { useCallback, useEffect, useId, useState } from "react";
import { ShieldCheck } from "lucide-react";
import {
  AppCard, AppCardHeader, AppCardTitle, AppCardDescription, AppCardContent,
  Radio, Switch, Callout,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import { useContentProtection } from "../../contexts/ContentProtectionContext";
import { ACCESS_COPY, ACCESS_LEVELS } from "../ContentProtection/contentAccess";

/**
 * Settings → Content Protection.
 *
 * Every control applies the moment it changes (they are switches, and a switch
 * promises "on now"), and rolls back with a toast if the save fails. Rules and
 * the meaning of each level: vc_be/utils/contentAccess.js.
 */
export default function ContentProtectionSettings({ showToast }) {
  const { refresh } = useContentProtection();
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const groupName = useId();

  useEffect(() => {
    let cancelled = false;
    apiClient.get("/institute/content-protection")
      .then(({ data }) => { if (!cancelled) setPolicy(data?.data || null); })
      .catch(() => { if (!cancelled) setPolicy({ fileAccess: "download", enforceFileAccess: false, blockCopy: false, blockDevTools: false }); });
    return () => { cancelled = true; };
  }, []);

  const save = useCallback(async (patch) => {
    const previous = policy;
    setPolicy((p) => ({ ...p, ...patch }));
    setSaving(true);
    try {
      await apiClient.put("/institute/content-protection", patch);
      refresh(); // upload forms read the new default straight away
      showToast?.("Content protection updated.");
    } catch (err) {
      setPolicy(previous);
      showToast?.(err?.response?.data?.message || "Could not save content protection.", "error");
    } finally {
      setSaving(false);
    }
  }, [policy, refresh, showToast]);

  if (!policy) return <div className="tw-mt-4"><Shimmer height={52} count={4} /></div>;

  const enforcedLabel = ACCESS_COPY[policy.fileAccess]?.label;

  return (
    <AppCard className="tw-mt-4">
      <AppCardHeader>
        <AppCardTitle>
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <ShieldCheck size={18} aria-hidden="true" /> Content protection
          </span>
        </AppCardTitle>
        <AppCardDescription>
          Stop students downloading or copying your course material. Staff are never restricted.
        </AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-6">
        <section className="tw-flex tw-flex-col tw-gap-3" aria-labelledby={`${groupName}-files`}>
          <h4 id={`${groupName}-files`} className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
            Files students open
          </h4>
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            The starting choice when anyone uploads a material or a document lesson. Each file can still be changed
            on its own.
          </p>
          <div className="tw-flex tw-flex-col tw-gap-2" role="radiogroup" aria-labelledby={`${groupName}-files`}>
            {ACCESS_LEVELS.map((level) => (
              <Radio
                key={level}
                name={groupName}
                value={level}
                checked={policy.fileAccess === level}
                // "Can download" has nothing to enforce, so it switches enforcement off too.
                onChange={() => save(level === "download" ? { fileAccess: level, enforceFileAccess: false } : { fileAccess: level })}
                disabled={saving}
                label={ACCESS_COPY[level].label}
                description={ACCESS_COPY[level].description}
              />
            ))}
          </div>
          <Switch
            checked={policy.enforceFileAccess}
            onChange={(enforceFileAccess) => save({ enforceFileAccess })}
            disabled={saving || policy.fileAccess === "download"}
            label="Apply to every file, including ones already uploaded"
            description={
              policy.fileAccess === "download"
                ? "Choose View only or stricter above to enforce it."
                : `Every file is at least "${enforcedLabel}". A file set stricter keeps its own setting.`
            }
          />
        </section>

        <section className="tw-flex tw-flex-col tw-gap-3" aria-labelledby={`${groupName}-lms`}>
          <h4 id={`${groupName}-lms`} className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
            Everywhere in the LMS
          </h4>
          <Switch
            checked={policy.blockCopy}
            onChange={(blockCopy) => save({ blockCopy })}
            disabled={saving}
            label="Block copying"
            description="Students can't select, copy, cut, drag or right-click any text in the LMS — pages, questions, documents. Typing and pasting into answers and the code editor still work."
          />
          <Switch
            checked={policy.blockDevTools}
            onChange={(blockDevTools) => save({ blockDevTools })}
            disabled={saving}
            label="Block developer tools"
            description="Closes the Inspect shortcuts (F12, Ctrl+Shift+I, Ctrl+U) and the right-click menu, and hides the LMS while a student has developer tools open."
          />
        </section>

        <Callout tone="info">
          These stop casual copying and sharing. No website can stop a phone camera or a determined expert, so every
          document and video also carries a watermark with the student's name — that is what makes a leak traceable.
        </Callout>
      </AppCardContent>
    </AppCard>
  );
}
